import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { History, ExternalLink, MousePointerClick, Globe, CheckCircle } from "lucide-react";
import TouchHistoryModal from "./TouchHistoryModal";

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function AttributionRow({ label, value, icon: Icon }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2">
      {Icon && <Icon className="mt-0.5 h-3.5 w-3.5 text-slate-400 flex-shrink-0" />}
      <div>
        <div className="text-xs text-slate-400">{label}</div>
        <div className="text-sm text-slate-200">{value}</div>
      </div>
    </div>
  );
}

function TouchSummaryCard({ title, timestamp, attribution, isFirst }) {
  const bgColor = isFirst ? "bg-emerald-500/10 ring-emerald-500/20" : "bg-indigo-500/10 ring-indigo-500/20";
  const textColor = isFirst ? "text-emerald-400" : "text-indigo-400";
  
  const campaign = attribution?.meta_campaign_name;
  const source = attribution?.lead_source_name;
  const utmSource = attribution?.utm_source;
  const utmMedium = attribution?.utm_medium;
  
  const hasUtm = utmSource || utmMedium;
  
  return (
    <div className={`rounded-lg p-3 ring-1 ${bgColor}`}>
      <div className="flex items-center justify-between mb-2">
        <span className={`text-xs font-medium ${textColor}`}>{title}</span>
        <span className="text-xs text-slate-400">{formatDate(timestamp)}</span>
      </div>
      <div className="space-y-1.5">
        {source && (
          <div className="flex items-center gap-1.5">
            <Globe className="h-3 w-3 text-slate-400" />
            <span className="text-sm text-slate-200">{source}</span>
          </div>
        )}
        {campaign && (
          <div className="flex items-center gap-1.5">
            <MousePointerClick className="h-3 w-3 text-slate-400" />
            <span className="text-sm text-slate-200">{campaign}</span>
          </div>
        )}
        {hasUtm && (
          <div className="text-xs text-slate-400">
            UTM: {utmSource || "—"} / {utmMedium || "—"}
          </div>
        )}
      </div>
    </div>
  );
}

export default function LeadAttributionPanel({ leadId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    if (!leadId) return;
    
    const fetchTouches = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(`/leads/${leadId}/touches`);
        setData(res.data);
      } catch (e) {
        // Not an admin or no touches - just show empty state
        if (e.response?.status === 403) {
          setError("Admin access required");
        } else {
          setError("Could not load attribution data");
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchTouches();
  }, [leadId]);

  if (loading) {
    return (
      <Card className="glass" data-testid="attribution-panel-loading">
        <CardContent className="p-4">
          <div className="animate-pulse space-y-3">
            <div className="h-4 w-24 bg-white/10 rounded" />
            <div className="h-16 bg-white/5 rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="glass" data-testid="attribution-panel-error">
        <CardContent className="p-4">
          <div className="text-sm text-slate-400">{error}</div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.touch_count === 0) {
    return (
      <Card className="glass" data-testid="attribution-panel-empty">
        <CardContent className="p-4">
          <div className="text-sm text-slate-400">No attribution data available</div>
        </CardContent>
      </Card>
    );
  }

  const { touch_count, first_touch_at, first_touch_attribution, latest_touch_at, latest_touch_attribution } = data;
  const showLatest = touch_count > 1;

  return (
    <>
      <Card className="glass" data-testid="attribution-panel">
        <CardContent className="p-0">
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <div className="flex items-center gap-2">
              <div className="text-lg font-semibold text-slate-100">Attribution</div>
              <Badge variant="secondary" className="bg-indigo-500/20 text-indigo-200">
                {touch_count} {touch_count === 1 ? "touch" : "touches"}
              </Badge>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setHistoryOpen(true)}
              className="text-indigo-300 hover:text-indigo-200 hover:bg-indigo-500/10"
              data-testid="view-touch-history-btn"
            >
              <History className="mr-1.5 h-4 w-4" />
              View Full History
            </Button>
          </div>
          
          <div className="border-t border-white/10 px-4 py-4">
            <div className={`grid gap-4 ${showLatest ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"}`}>
              {/* First Touch */}
              <TouchSummaryCard
                title="First Touch"
                timestamp={first_touch_at}
                attribution={first_touch_attribution}
                isFirst={true}
              />
              
              {/* Latest Touch (only if different from first) */}
              {showLatest && (
                <TouchSummaryCard
                  title="Latest Touch"
                  timestamp={latest_touch_at}
                  attribution={latest_touch_attribution}
                  isFirst={false}
                />
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <TouchHistoryModal
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        leadId={leadId}
        leadName={data.lead_name}
      />
    </>
  );
}
